<template>
  <div class="outgoing-detail">
    <Toast />
    <div v-if="loading" class="loading-container">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p>Memuat data...</p>
    </div>

    <div v-if="!loading && detail != null" class="card">
      <h5>
        <b>Detail Pembelian</b>
      </h5>

      <!-- Summary Information -->
      <div class="info-grid">
        <div class="info-item">
          <label>Gudang</label>
          <div>
            <span
              v-if="
                detail.summary.warehouse_code && detail.summary.warehouse_name
              "
            >
              {{ detail.summary.warehouse_code }} -
              {{ detail.summary.warehouse_name }}
            </span>
            <span v-else-if="detail.summary.warehouse_code">
              {{ detail.summary.warehouse_code }}
            </span>
            <span v-else>-</span>
          </div>
        </div>
        <div class="info-item">
          <label>Supplier</label>
          <div>
            <span
              v-if="
                detail.summary.supplier_code && detail.summary.supplier_name
              "
            >
              {{ detail.summary.supplier_code }} -
              {{ detail.summary.supplier_name }}
            </span>
            <span v-else-if="detail.summary.supplier_code">
              {{ detail.summary.supplier_code }}
            </span>
            <span v-else>-</span>
          </div>
        </div>
        <div class="info-item">
          <label>Nomor Bon</label>
          <div>{{ detail.summary.doc_number }}</div>
        </div>
        <div class="info-item">
          <label>Tanggal Bon</label>
          <div>
            {{ moment(detail.summary.doc_date).format('DD-MM-YYYY') }}
          </div>
        </div>
        <div class="info-item" v-if="detail.summary.mutation_code != ''">
          <label>Kode Mutasi</label>
          <div>{{ detail.summary.mutation_code }}</div>
        </div>
        <div class="info-item">
          <label>Total Barang</label>
          <div>{{ detail.summary.total_item }}</div>
        </div>
        <div class="info-item">
          <label>Total Qty</label>
          <div>{{ detail.summary.total_qty | number }}</div>
        </div>
        <div class="info-item">
          <label>Total Harga</label>
          <div>{{ detail.summary.total_price | currency }}</div>
        </div>
      </div>

      <!-- DataTable for Purchase Orders -->
      <div class="p-col-12 p-mt-4">
        <div style="overflow-x: auto; width: 100%">
          <DataTable
            :value="detail.purchase_orders"
            class="p-datatable-sm"
            :loading="loading"
            :scrollable="true"
            scrollDirection="horizontal"
            scrollHeight="550px"
            style="min-width: 100%"
          >
            <template #empty>
              <div class="empty-state">
                <i class="pi pi-inbox mb-3" style="font-size: 2rem"></i>
                <p>Data tidak tersedia</p>
              </div>
            </template>

            <!-- Fixed Columns -->
            <Column
              field="no"
              header="No"
              :sortable="false"
              headerStyle="width: 3rem; min-width: 3rem;"
              bodyStyle="width: 3rem; min-width: 3rem;"
            >
              <template #body="slotProps">
                {{ slotProps.index + 1 }}
              </template>
            </Column>

            <Column
              field="item_code"
              header="Kode Barang"
              :sortable="false"
              headerStyle="width: 10rem; min-width: 10rem;"
              bodyStyle="width: 10rem; min-width: 10rem;"
            >
            </Column>

            <Column
              field="item_name"
              header="Nama Barang"
              :sortable="false"
              headerStyle="width: 18rem; min-width: 18rem;"
              bodyStyle="width: 18rem; min-width: 18rem;"
            >
              <template #body="slotProps">
                <span :title="slotProps.data.item_name">
                  {{ truncateText(slotProps.data.item_name, 30) }}
                </span>
              </template>
            </Column>

            <Column
              field="purchase_order_qty"
              header="Qty Order"
              :sortable="false"
              headerStyle="width: 6rem; min-width: 6rem;"
              bodyStyle="width: 6rem; min-width: 6rem;"
            >
              <template #body="slotProps">
                {{ slotProps.data.purchase_order_qty }}
              </template>
            </Column>

            <Column
              field="purchase_order_price"
              header="Harga Modal"
              :sortable="false"
              headerStyle="width: 8rem; min-width: 8rem; text-align: right;"
              bodyStyle="width: 8rem; min-width: 8rem; text-align: right;"
            >
              <template #body="slotProps">
                {{ slotProps.data.purchase_order_price | currency }}
              </template>
            </Column>

            <Column
              field="distribution_price"
              header="Harga"
              :sortable="false"
              headerStyle="width: 8rem; min-width: 8rem; text-align: right;"
              bodyStyle="width: 8rem; min-width: 8rem; text-align: right;"
            >
              <template #body="slotProps">
                {{ slotProps.data.distribution_price | currency }}
              </template>
            </Column>

            <Column
              field="total_distributed_qty"
              header="Total Qty"
              :sortable="false"
              headerStyle="width: 6rem; min-width: 6rem;"
              bodyStyle="width: 6rem; min-width: 6rem;"
            >
              <template #body="slotProps">
                {{ slotProps.data.total_distributed_qty || 0 }}
              </template>
            </Column>

            <Column
              field="remaining_qty"
              header="Sisa"
              :sortable="false"
              headerStyle="width: 6rem; min-width: 6rem;"
              bodyStyle="width: 6rem; min-width: 6rem;"
            >
              <template #body="slotProps">
                {{ slotProps.data.remaining_qty || 0 }}
              </template>
            </Column>

            <!-- Dynamic Outlet Columns -->
            <Column
              v-for="outlet in uniqueOutlets"
              :key="`outlet-${outlet.id}`"
              :field="`outlet_${outlet.id}_qty`"
              :header="outlet.alias"
              :sortable="false"
              headerStyle="width: 6rem; min-width: 6rem; text-align: center;"
              bodyStyle="width: 6rem; min-width: 6rem; text-align: center;"
            >
              <template #body="slotProps">
                {{ slotProps.data[`outlet_${outlet.id}_qty`] || 0 }}
              </template>
            </Column>

            <Column
              field="distribution_date"
              header="Tanggal"
              :sortable="false"
              headerStyle="width: 10rem; min-width: 10rem;"
              bodyStyle="width: 10rem; min-width: 10rem;"
            >
              <template #body="slotProps">
                {{ formatDistributionDate(slotProps.data.distribution_date) }}
              </template>
            </Column>

            <Column
              field="distribution_status"
              header="Status"
              :sortable="false"
              headerStyle="width: 8rem; min-width: 8rem;"
              bodyStyle="width: 8rem; min-width: 8rem;"
            >
              <template #body="slotProps">
                <span
                  :class="`distribution-status status-${slotProps.data.distribution_status}`"
                >
                  {{
                    getDistributionStatusLabel(
                      slotProps.data.distribution_status
                    )
                  }}
                </span>
              </template>
            </Column>

            <Column
              field="distributed_qty"
              header="Dist Qty"
              :sortable="false"
              headerStyle="width: 6rem; min-width: 6rem;"
              bodyStyle="width: 6rem; min-width: 6rem;"
            >
              <template #body="slotProps">
                {{ slotProps.data.distributed_qty || 0 }}
              </template>
            </Column>

            <Column
              field="category"
              header="Kategori"
              :sortable="false"
              headerStyle="width: 15rem; min-width: 15rem;"
              bodyStyle="width: 15rem; min-width: 15rem;"
            >
              <template #body="slotProps">
                <span
                  v-if="
                    slotProps.data.category_code && slotProps.data.category_name
                  "
                >
                  {{ slotProps.data.category_code }} -
                  {{ slotProps.data.category_name }}
                </span>
                <span v-else-if="slotProps.data.category_code">
                  {{ slotProps.data.category_code }}
                </span>
                <span v-else-if="slotProps.data.category_name">
                  {{ slotProps.data.category_name }}
                </span>
                <span v-else>-</span>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <div v-if="!loading && detail == null" class="card">
      <div class="empty-state">
        <i class="pi pi-inbox mb-3" style="font-size: 2rem"></i>
        <p>Data tidak ditemukan</p>
      </div>
    </div>
  </div>
</template>

<script>
const moment = require('moment')
export default {
  data() {
    return {
      moment: moment,
      loading: true,
      detail: null,
      listOutlets: null,
      uniqueOutlets: []
    }
  },
  mounted() {
    // Validate mutation_code query parameter
    if (!this.$route.query.mutation_code) {
      this.showToast(
        'error',
        'Error',
        'Kode mutasi tidak ditemukan. Silakan kembali ke halaman sebelumnya.'
      )
      this.loading = false
      return
    }

    this.getPurchaseOrderDetail()
    this.getListOutlets()
  },
  methods: {
    showToast(severity, summary, detail) {
      this.$toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 3000
      })
    },

    checkStatus(status) {
      if (status === 1) {
        return 'Confirmed'
      }
      return 'Pending'
    },

    printPage() {
      if (!this.detail) {
        this.showToast(
          'warn',
          'Peringatan',
          'Data tidak tersedia untuk dicetak'
        )
        return
      }
      this.$router.push({
        name: 'print-purchase',
        params: { detail: this.detail, listOutlets: this.listOutlets }
      })
    },

    async getInitialPurchaseOrderId() {
      try {
        const params = new URLSearchParams({
          mutation_code: this.$route.query.mutation_code,
          limit: '1'
        })

        const response = await this.$http.get(
          `${
            process.env.VUE_APP_API_URL
          }/dashboard/initial-purchase-orders?${params.toString()}`
        )

        if (response.status === 200) {
          const initialOrders = response.data.data.initial_purchase_orders || []
          if (initialOrders.length > 0) {
            return initialOrders[0].id
          }
        }
        return null
      } catch (error) {
        console.error('Error fetching initial purchase order ID:', error)
        return null
      }
    },

    async getPurchaseOrderDetail() {
      this.loading = true
      try {
        // Get init_id from doc_number
        const initId = await this.getInitialPurchaseOrderId()
        if (!initId) {
          this.showToast('warn', 'Peringatan', 'Data pembelian tidak ditemukan')
          this.loading = false
          return
        }

        // Call new distribution endpoint
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/dashboard/purchase-orders/distribution?init_id=${initId}`
        )

        if (response.status === 200) {
          const responseData = response.data.data
          this.detail = responseData
          await this.transformDataForDisplay()
        } else {
          throw new Error('Gagal mengambil data distribusi pembelian')
        }
      } catch (error) {
        console.error('Error fetching purchase order distribution:', error)
        const errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Gagal mengambil data pembelian. Silakan coba lagi.'
        this.showToast('error', 'Error', errorMessage)
        this.detail = null
      } finally {
        this.loading = false
      }
    },

    async transformDataForDisplay() {
      if (!this.detail || !this.detail.initial_purchase_order) return

      const { initial_purchase_order, purchase_orders } = this.detail

      // Get unique purchase order items (by id) for summary calculation
      const uniqueItems = this.getUniquePurchaseOrderItems(purchase_orders)

      // Calculate summary
      this.detail.summary = {
        warehouse_code: initial_purchase_order.warehouse_code || '',
        warehouse_name: initial_purchase_order.warehouse_name || '',
        supplier_code: initial_purchase_order.supplier_code || '',
        supplier_name: initial_purchase_order.supplier_name || '',
        mutation_code: initial_purchase_order.mutation_code || '',
        doc_number: initial_purchase_order.doc_number || '',
        doc_date: initial_purchase_order.doc_date || '',
        total_item: uniqueItems.length,
        total_qty: this.calculateTotalQty(uniqueItems),
        total_price: this.calculateTotalPrice(uniqueItems)
      }

      // Ensure outlets are loaded before getting unique outlets
      if (!this.listOutlets || this.listOutlets.length === 0) {
        await this.getListOutlets()
      }

      // Get unique outlets
      const uniqueOutlets = this.getAllUniqueOutlets()
      this.uniqueOutlets = uniqueOutlets

      // Transform purchase orders to include outlet qty columns
      purchase_orders.forEach(item => {
        // Create outlet distribution map for this row
        const outletDistMap = new Map()

        if (item.distributions && item.distributions.length > 0) {
          item.distributions.forEach(dist => {
            outletDistMap.set(dist.outlet_id, dist.qty)
          })

          // Get price from the first distribution (since each row represents one distribution)
          // If there are multiple distributions, we'll use the first one's price
          if (item.distributions.length > 0) {
            item.distribution_price = item.distributions[0].price || 0
          }
        } else {
          item.distribution_price = 0
        }

        // Add outlet qty properties to item
        uniqueOutlets.forEach(outlet => {
          item[`outlet_${outlet.id}_qty`] = outletDistMap.get(outlet.id) || 0
        })
      })
    },

    getAllUniqueOutlets() {
      const outletMap = new Map()

      // If listOutlets is not available, fall back to collecting from distributions only
      if (!this.listOutlets || this.listOutlets.length === 0) {
        if (!this.detail || !this.detail.purchase_orders) {
          return []
        }

        // Fallback: collect outlets from distributions only
        this.detail.purchase_orders.forEach(item => {
          if (item.distributions && item.distributions.length > 0) {
            item.distributions.forEach(dist => {
              if (!outletMap.has(dist.outlet_id)) {
                outletMap.set(dist.outlet_id, {
                  id: dist.outlet_id,
                  code: dist.outlet_code || '',
                  name: dist.outlet_name || '',
                  alias: dist.outlet_code || '-'
                })
              }
            })
          }
        })

        return Array.from(outletMap.values()).sort((a, b) =>
          a.code.localeCompare(b.code)
        )
      }

      // Primary logic: Use all outlets from listOutlets
      // First, add all outlets from listOutlets
      this.listOutlets.forEach(outlet => {
        outletMap.set(outlet.id, {
          id: outlet.id,
          code: outlet.code || '',
          name: outlet.name || '',
          alias: outlet.alias || outlet.code || '-'
        })
      })

      // Then, enrich with distribution data if available (for code/name, but keep alias from listOutlets)
      if (this.detail && this.detail.purchase_orders) {
        this.detail.purchase_orders.forEach(item => {
          if (item.distributions && item.distributions.length > 0) {
            item.distributions.forEach(dist => {
              if (outletMap.has(dist.outlet_id)) {
                // Update with distribution data if available, but keep alias from listOutlets
                const outlet = outletMap.get(dist.outlet_id)
                if (dist.outlet_code) {
                  outlet.code = dist.outlet_code
                }
                if (dist.outlet_name) {
                  outlet.name = dist.outlet_name
                }
                // Keep alias from listOutlets (preferred source)
              }
            })
          }
        })
      }

      // Sort by outlet code
      return Array.from(outletMap.values()).sort((a, b) =>
        a.code.localeCompare(b.code)
      )
    },

    getUniquePurchaseOrderItems(purchaseOrders) {
      const seen = new Set()
      return purchaseOrders.filter(item => {
        if (seen.has(item.id)) {
          return false
        }
        seen.add(item.id)
        return true
      })
    },

    calculateTotalQty(uniqueItems) {
      return uniqueItems.reduce(
        (sum, item) => sum + parseInt(item.purchase_order_qty || 0),
        0
      )
    },

    calculateTotalPrice(uniqueItems) {
      return uniqueItems.reduce((sum, item) => {
        const qty = parseInt(item.purchase_order_qty || 0)
        const price = parseFloat(item.purchase_order_price || 0)
        return sum + qty * price
      }, 0)
    },

    getDistributionStatusLabel(status) {
      const labels = {
        none: 'Pending',
        partial: 'Sebagian',
        complete: 'Lengkap'
      }
      return labels[status] || status
    },

    formatDistributionDate(date) {
      if (!date) return '-'
      return moment(date).format('DD-MM-YYYY')
    },

    hasDistributions(item) {
      return item.distributions && item.distributions.length > 0
    },

    truncateText(text, maxLength = 30) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    async getListOutlets() {
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/outlets?page=1&limit=100`
        )

        if (response.status === 200) {
          this.listOutlets = response.data.data.outlets || []
        }
      } catch (error) {
        console.error('Error fetching outlets:', error)
        // Don't show error toast for outlets as it's not critical
        this.listOutlets = []
      }
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .p-progressbar {
  height: 0.5rem;
  background-color: #d8dadc;
  .p-progressbar-value {
    background-color: #607d8b;
  }
}
.p-datatable .p-column-filter {
  display: none;
}
.table-header {
  display: flex;
  justify-content: space-between;
}
/deep/ .p-datatable.p-datatable-outlets {
  .p-datatable-header {
    padding: 1rem;
    text-align: left;
    font-size: 1.5rem;
  }
  .p-paginator {
    padding: 1rem;
  }
  .p-datatable-thead > tr > th {
    text-align: left;
  }
  .p-datatable-tbody > tr > td {
    cursor: auto;
  }
  .p-dropdown-label:not(.p-placeholder) {
    text-transform: uppercase;
  }
}
/* Responsive */
.p-datatable-outlets .p-datatable-tbody > tr > td .p-column-title {
  display: none;
}
.customer-badge {
  border-radius: 2px;
  padding: 0.25em 0.5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  &.status-qualified {
    background: #c8e6c9;
    color: #256029;
  }
  &.status-unqualified {
    background: #ffcdd2;
    color: #c63737;
  }
  &.status-negotiation {
    background: #feedaf;
    color: #8a5340;
  }
  &.status-new {
    background: #b3e5fc;
    color: #23547b;
  }
  &.status-renewal {
    background: #eccfff;
    color: #694382;
  }
  &.status-proposal {
    background: #ffd8b2;
    color: #805b36;
  }
}
.p-progressbar-value.ui-widget-header {
  background: #607d8b;
}
@media (max-width: 640px) {
  .p-progressbar {
    margin-top: 0.5rem;
  }
}
.product-image {
  width: 100px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
}
.orders-subtable {
  padding: 1rem;
}
.product-badge {
  border-radius: 2px;
  padding: 0.25em 0.5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  &.status-instock {
    background: #c8e6c9;
    color: #256029;
  }
  &.status-outofstock {
    background: #ffcdd2;
    color: #c63737;
  }
  &.status-lowstock {
    background: #feedaf;
    color: #8a5340;
  }
}
input:disabled {
  color: black;
}
.order-badge {
  border-radius: 2px;
  padding: 0.25em 0.5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  &.order-delivered {
    background: #c8e6c9;
    color: #256029;
  }
  &.order-cancelled {
    background: #ffcdd2;
    color: #c63737;
  }
  &.order-pending {
    background: #feedaf;
    color: #8a5340;
  }
  &.order-returned {
    background: #eccfff;
    color: #694382;
  }
}
@media screen and (max-width: 960px) {
  /deep/ .p-datatable {
    &.p-datatable-outlets {
      .p-datatable-thead > tr > th,
      .p-datatable-tfoot > tr > td {
        display: none !important;
      }
      .p-datatable-tbody > tr {
        > td {
          text-align: left;
          display: block;
          border: 0 none !important;
          width: 100% !important;
          float: left;
          clear: left;
          .p-column-title {
            padding: 0.4rem;
            min-width: 30%;
            display: inline-block;
            margin: -0.4rem 1rem -0.4rem -0.4rem;
            font-weight: bold;
          }
          .p-progressbar {
            margin-top: 0.5rem;
          }
        }
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.loading-container {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;

  .info-item {
    label {
      display: block;
      font-size: 0.75rem;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }

    div {
      font-size: 0.875rem;
      color: #495057;
      font-weight: 500;
    }
  }

  // Responsive: 2 columns on medium screens, 1 column on small screens
  @media screen and (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.status-badge {
  border-radius: 2px;
  padding: 0.25em 0.5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;

  &.status-success {
    background: #c8e6c9;
    color: #256029;
  }

  &.status-warning {
    background: #feedaf;
    color: #8a5340;
  }
}

.distribution-status {
  border-radius: 2px;
  padding: 0.25em 0.5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;

  &.status-none {
    background: #ffcdd2;
    color: #c63737;
  }

  &.status-partial {
    background: #feedaf;
    color: #8a5340;
  }

  &.status-complete {
    background: #c8e6c9;
    color: #256029;
  }
}

// Horizontal scrolling styles
/deep/ .p-datatable {
  .p-datatable-wrapper {
    overflow-x: auto;
  }

  .p-datatable-thead > tr > th,
  .p-datatable-tbody > tr > td {
    white-space: nowrap;
    border-right: 1px solid #dee2e6;
  }

  .p-datatable-thead > tr > th:last-child,
  .p-datatable-tbody > tr > td:last-child {
    border-right: none;
  }

  .p-datatable-thead > tr > th {
    border-bottom: 1px solid #dee2e6;
  }

  .p-datatable-tbody > tr > td {
    border-bottom: 1px solid #dee2e6;
  }

  .p-datatable-tbody > tr:last-child > td {
    border-bottom: none;
  }
}

/deep/ .p-datatable .p-datatable-tfoot > tr > td {
  padding: 1rem;
  border-top: 2px solid #dee2e6;
  background-color: #f8f9fa;
}

/deep/ .p-datatable .p-datatable-tfoot > tr > td:first-child {
  text-align: left;
}

/deep/ .p-datatable .p-datatable-tfoot > tr > td:nth-child(7),
/deep/ .p-datatable .p-datatable-tfoot > tr > td:nth-child(8) {
  text-align: right;
}
</style>
